import { ArrowRight } from 'lucide-react';
import { useState } from 'react';

import { useGetDraftResumeQuery } from '../../services/resumeBuilderApi';
import Button from '../Common/Button';
import CommonDialog from '../Common/Dialog';
import OptionCard from '../ReUsableComponents/OptionCard';

import UploadResume from './UploadResume';

type Props = {
  onNext: () => void;
  onContinueDraft?: (draft: any) => void;
};

const ResumeBuilderMethod: React.FC<Props> = ({
  onNext,
  onContinueDraft
}: Props) => {
  const [openPopUP, setIsOpenPopUP] = useState(false);
  const {
    data: draftResume,
    isLoading,
    isError,
    isSuccess
  } = useGetDraftResumeQuery({}, { refetchOnMountOrArgChange: true });

  const draft = draftResume?.data;
  const hasDraft =
    isSuccess &&
    !isError &&
    !!draft &&
    !draft.resumeGenerated &&
    (draft.resumeFormState?.currentStep || draft.id);

  return (
    <div className='bg-gamma'>
      {!isLoading && !isError && hasDraft && (
        <div className='mb-6 flex flex-col items-start justify-between gap-3 rounded-xl border border-primary/30 bg-gamma p-4 shadow-sm md:flex-row md:items-center'>
          <div>
            <h4 className='text-sm font-bold text-primary'>
              Continue where you left off
            </h4>
            <p className='text-xs text-gray-600'>
              We found a draft of your resume
              {draft?.title ? ` — “${draft.title}”` : ''}. Pick up right where
              you stopped.
            </p>
          </div>

          <Button
            label='Continue where you left'
            variant='contained'
            handleClickBtn={() => onContinueDraft?.(draft)}
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      )}
      {/* Header */}
      <div className='mb-6 text-center lg:text-left'>
        <p className='text-[11px] font-semibold uppercase tracking-wider text-primary'>
          Step 01
        </p>
        <h1 className='mb-2 text-xl font-extrabold lg:text-2xl'>
          Let’s get started
        </h1>
        <p className='text-sm text-gray-500'>
          How do you want to create your resume?
        </p>
      </div>

      {/* Options Grid */}
      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        <OptionCard
          icon='ai'
          title='Create with AI assistance'
          description='Let our advanced algorithms guide your content for maximum impact and ATS visibility.'
          action='AI-guided process'
          highlighted
          onClick={onNext}
        />

        <OptionCard
          icon='upload'
          title='Upload resume'
          description="Existing resume? We'll parse your data and re-format it into a modern layout."
          action='Import from PDF or Word'
          onClick={() => setIsOpenPopUP(true)}
        />

        <OptionCard
          icon='new'
          title='Create new resume'
          description='Build your professional identity from the ground up with our optimized editor.'
          action='Start from scratch'
          onClick={onNext}
          disabled={true}
        />

        <OptionCard
          icon='linkedin'
          title='Create with LinkedIn profile'
          description='Instantly sync your work history and skills directly from your professional profile.'
          action='Sync your data'
          onClick={onNext}
          disabled={true}
        />
      </div>

      {/* Bottom Section */}
      <div className='mt-10 grid grid-cols-1 items-center gap-6 rounded-xl bg-lightGrey p-6 lg:grid-cols-3'>
        <div className='lg:col-span-2'>
          <h4 className='mb-1 text-lg font-bold'>Not sure where to start?</h4>
          <p className='text-xs text-gray-600'>
            Our AI assistance is the most popular choice for professionals
            looking to optimize their career trajectory with industry-specific
            keywords.
          </p>
        </div>

        <div className='flex justify-center lg:justify-end'>
          <div className='h-20 w-20 overflow-hidden rounded-full border-4 border-white shadow-md'>
            <img
              src='https://lh3.googleusercontent.com/aida-public/AB6AXuAXdlh21JRNyJr_PzTXTmhx6QGvVAxtLJ-67Kkubpnwu41RVclJ_aKAIq8788ugEDer-9c7jjazqoOml_iZFEXk0jk338JwcGgjXK6JYk_qTDdFZcY3u1WqjsLbk7UqSEmjSE9hNaUx5xI33tLlizP-fAn4K8vvLtB6pCNkHmrcrkcCaw5iXZysTSEgdOK6fInl09bXjp0XfC_5CHpXnubTyd2kmGtO-TkW0aC95t86GdU0hrRkWiuNGe7pdWXOuKkkmkzS-OLkxU-I'
              alt='AI advisor'
              className='h-full w-full object-cover'
            />
          </div>
        </div>
      </div>
      <CommonDialog close={() => setIsOpenPopUP(false)} isOpen={openPopUP}>
        <UploadResume onNext={onNext} />
      </CommonDialog>
    </div>
  );
};

export default ResumeBuilderMethod;
